import { Link } from 'react-router-dom'
import { styled, CSS, css } from 'theme'
import { Flex } from './Flex'
import { ReactNode } from 'react'

const TextVariants = {
  size: {
    '12': {
      fontSize: '$12',
    },
    '14': {
      fontSize: '$14',
    },
    '18': {
      fontSize: '$18',
    },
    '20': {
      fontSize: '$20',
    },
    '24': {
      fontSize: '$24',
    },
    '28': {
      fontSize: '$28',
    },
    '32': {
      fontSize: '$32',
    },
    '36': {
      fontSize: '$36',
    },
  },
  color: {
    primary: {
      color: '$primaryBase',
    },
    secondary: {
      color: '$secondaryBase',
    },
    warning: {
      color: '$warning500',
    },
    error: {
      color: '$error500',
    },
    success: {
      color: '$success500',
    },
    gray: {
      color: '$text',
    },
    white: {
      color: '$whiteBase',
    },
    black: {
      color: '$blackBase',
    },
    darkGray: {
      color: '$gray900',
    },
    textBlack: {
      color: '$black02',
    },
    disabled: {
      color: '$disabledText',
    },
    transparent: {},
  },
}

const TextDefaultVariants = {
  size: '$14',
  color: '$blackBase',
}

const TextBase = styled('span', {
  margin: 0,
  color: '$blackBase',
  letterSpacing: 'normal',
  variants: {
    color: TextVariants.color,
  },
})

const TextDefault = styled(TextBase, {
  variants: TextVariants,
  defaultVariants: TextDefaultVariants,
})

export const h1Styles = {
  fontSize: '$36',
  fontWeight: '$semibold',
  lineHeight: '$lineHeight$44',
}

export const h1StylesExtend = {
  fontSize: '$44',
  fontWeight: '$semibold',
  lineHeight: '$lineHeight$66',
}

export const h2Styles = {
  fontSize: '$28',
  fontWeight: '$semibold',
  lineHeight: '$lineHeight$44',
}

export const h2StylesExtend = {
  fontSize: '$36',
  fontWeight: '$semibold',
  lineHeight: '$lineHeight$44',
}

export const h3Styles = {
  fontSize: '$20',
  fontWeight: '$semibold',
  lineHeight: '$lineHeight$30',
}

export const h3StylesExtend = {
  fontSize: '$24',
  fontWeight: '$bold',
  lineHeight: '$lineHeight$32',
}

export const h4Styles = {
  fontSize: '$18',
  fontWeight: '$semibold',
  lineHeight: '$lineHeight$28',
}

export const h5Styles = {
  fontSize: '$16',
  fontWeight: '$medium',
  lineHeight: '$lineHeight$24',
}

export const h6Styles = {
  fontSize: '$14',
  fontWeight: '$medium',
  lineHeight: '$lineHeight$20',
}

export const labelStyles = {
  fontSize: '$14',
  fontWeight: '$medium',
  lineHeight: '$lineHeight$20',
  textAlign: 'left',
  mb: '$8',
  color: '$inputLabel',
}

export const paraStyles = {
  fontSize: '$14',
  fontWeight: '$normal',
  lineHeight: '$lineHeight$20',
  color: '$text',
}

export const paraStylesExtend = {
  fontSize: '$16',
  fontWeight: '$normal',
  lineHeight: '$lineHeight$24',
  color: '$text',
}

export const anchorStyles = {
  'fontSize': '$14',
  'fontWeight': '$medium',
  'lineHeight': '$lineHeight$24',
  'color': '$primaryBase',
  'textDecoration': 'none',
  'cursor': 'pointer',

  '&:hover': {
    color: '$primaryBgHover',
  },
}

export const anchorStylesExtend = {
  'fontSize': '$16',
  'fontWeight': '$semibold',
  'lineHeight': '$lineHeight$24',
  'color': '$primaryBase',
  'textDecoration': 'none',
  'cursor': 'pointer',

  '&:hover': {
    color: '$primaryBgHover',
  },
}

export const ellipsisStyles = {
  'display': '-webkit-box',
  '-webkit-line-clamp': 4,
  '-webkit-box-orient': 'vertical',
  'overflow': 'hidden',
  'text-overflow': 'ellipsis',
}

export const tooltipStyles = {
  fontSize: '$12',
  fontWeight: '$semibold',
  lineHeight: '$lineHeight$18',
}

const H1 = styled(TextBase, h1Styles)
const H1Extended = styled(TextBase, h1StylesExtend)
const H2 = styled(TextBase, h2Styles)
const H2Extended = styled(TextBase, h2StylesExtend)
const H3 = styled(TextBase, h3Styles)
const H3Extended = styled(TextBase, h3StylesExtend)
const H4 = styled(TextBase, h4Styles)
const H5 = styled(TextBase, h5Styles)
const H6 = styled(TextBase, h6Styles)
const LABEL = styled(TextBase, labelStyles)
const PARA = styled(TextBase, paraStyles)
const PARAExtended = styled(TextBase, paraStylesExtend)
export const ANCHOR = styled(TextBase, Link, anchorStyles)
const ANCHORExtended = styled(TextBase, Link, anchorStylesExtend)
const TOOLTIP = styled(TextBase, tooltipStyles)

export type TextColorProps =
  | 'primary'
  | 'secondary'
  | 'warning'
  | 'error'
  | 'success'
  | 'gray'
  | 'white'
  | 'black'
  | 'darkGray'

export type TextAsProps =
  | 'h1'
  | 'h1Extend'
  | 'h2'
  | 'h2Extend'
  | 'h3'
  | 'h3Extend'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'pExtend'
  | 'a'
  | 'aExtend'
  | 'label'
  | 'tooltip'
  | 'helperText'
  | 'span'

interface TextComponentProps {
  dangerouslySetInnerHTML?: {
    __html: string
  }
  className?: string
  css?: CSS
  styleBox?: CSS
  as?: TextAsProps
  children?: ReactNode
  color?: keyof typeof TextVariants.color
  shrink?: boolean
  htmlFor?: string
  href?: string
  icon?: {
    Before?: ReactNode
    After?: ReactNode
    gap?: CSS
  }
}

const generateTextElements = (
  as: TextComponentProps['as'],
  children: React.ReactNode,
  rest: Omit<TextComponentProps, 'as' | 'children'>
) => {
  switch (as) {
    case 'h1':
      return (
        <H1 as="h1" {...rest}>
          {children}
        </H1>
      )
    case 'h1Extend':
      return (
        <H1Extended as="h1" {...rest}>
          {children}
        </H1Extended>
      )
    case 'h2':
      return (
        <H2 as="h2" {...rest}>
          {children}
        </H2>
      )
    case 'h2Extend':
      return (
        <H2Extended as="h2" {...rest}>
          {children}
        </H2Extended>
      )
    case 'h3':
      return (
        <H3 as="h3" {...rest}>
          {children}
        </H3>
      )
    case 'h3Extend':
      return (
        <H3Extended as="h3" {...rest}>
          {children}
        </H3Extended>
      )
    case 'h4':
      return (
        <H4 as="h4" {...rest}>
          {children}
        </H4>
      )
    case 'h5':
      return (
        <H5 as="h5" {...rest}>
          {children}
        </H5>
      )
    case 'h6':
      return (
        <H6 as="h6" {...rest}>
          {children}
        </H6>
      )
    case 'p':
      return (
        <PARA as="p" {...rest}>
          {children}
        </PARA>
      )
    case 'pExtend':
      return (
        <PARAExtended as="p" {...rest}>
          {children}
        </PARAExtended>
      )
    case 'a':
      return (
        <ANCHOR as="a" {...rest} href={rest.href}>
          {children}
        </ANCHOR>
      )
    case 'aExtend':
      return (
        <ANCHORExtended as="a" {...rest} href={rest.href}>
          {children}
        </ANCHORExtended>
      )
    case 'label':
      return (
        <LABEL as="label" {...rest}>
          {children}
        </LABEL>
      )
    case 'tooltip':
      return (
        <TOOLTIP as="span" {...rest}>
          {children}
        </TOOLTIP>
      )
    default:
      return <TextDefault {...rest}>{children}</TextDefault>
  }
}

export const Text = ({
  as,
  children,
  icon,
  styleBox,
  ...rest
}: TextComponentProps) => {
  const iconStyle = css({
    ...(icon?.Before && { marginRight: '$space$8' }),
    ...(icon?.After && { marginLeft: '$space$8' }),
  })

  const textElements = generateTextElements(as, children, rest)

  return (
    <>
      {icon ? (
        <Flex css={styleBox} align={'center'}>
          {icon?.Before && (
            <Flex
              align={'center'}
              className={iconStyle.toString()}
              css={icon?.gap}
              children={icon.Before}
            />
          )}
          {textElements}
          {icon?.After && (
            <Flex
              align={'center'}
              className={iconStyle.toString()}
              css={icon?.gap}
              children={icon.After}
            />
          )}
        </Flex>
      ) : (
        textElements
      )}
    </>
  )
}
